import type { CSSProperties, ReactNode } from 'react';
import { darkenForWhiteText, isHexColor } from '../../utils/color';

/** Convert a #RRGGBB hex string to an rgba() string at the given alpha. */
export function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Inline style for a status badge driven by a configurable hex color: a solid
 * background in that color with white text. A pale color (a light yellow or
 * mint) is darkened just enough for the white text to stay readable, so what
 * Settings holds is always legible. Falls back to a neutral gray when the color
 * is missing or not a hex value. Every expense and planning status badge goes
 * through this so colors render identically.
 */
export function statusBadgeStyle(color: string | null | undefined): CSSProperties {
  if (!isHexColor(color)) {
    return { backgroundColor: '#F3F4F6', color: '#374151' };
  }
  return { backgroundColor: darkenForWhiteText(color), color: '#FFFFFF' };
}

export interface StatusPillProps {
  label: ReactNode;
  /** Configurable hex color (e.g. "#3B82F6"); falls back to gray when absent. */
  color?: string | null;
  className?: string;
}

/** A pill badge tinted by a configurable hex status color. */
export function StatusPill({ label, color, className }: StatusPillProps) {
  return (
    <span
      style={statusBadgeStyle(color)}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${className ?? ''}`}
    >
      {label}
    </span>
  );
}
